import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useDialogs, EventPreviewPopper, useClientOptions, useUsers } from '@paintscout/ui-business'
import React from 'react'
import type { EventProps } from 'react-big-calendar'
import moment from 'moment-timezone'
import type { CalendarEvent } from 'paintscout'
import { useCancelEventFlow } from 'src/hooks'
import history from 'shared/util/routing/history'
import classnames from 'classnames'
import { formatName, translateEventType } from '@paintscout/util/builder'
import { getEventLabel } from '@paintscout/util'
import { EventDialog } from '../../../dialogs'
import { useCalendar } from '../CalendarContext'
import { GOOGLE_CALENDAR_EVENT_TYPE } from '../calendarUtils'
import type { CrewPalette } from '../../ProductionCalendar/util'
import { getCrewPalette } from '../../ProductionCalendar/util'
import { getUnavailableStripeBackground, unavailableBlurOverlay } from '../unavailableEventStyles'

interface DayEventStyleProps extends CrewPalette {
  miniEvent?: boolean
}

const useStyles = makeStyles<Theme, DayEventStyleProps>((theme) => ({
  root: {
    boxSizing: 'border-box',
    height: 'calc(100% - 6px)',
    width: 'calc(100% - 8px)',
    marginTop: '3px',
    marginBottom: '3px',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    borderRadius: theme.borderRadius.md,
    backgroundColor: ({ chipBg }) => chipBg,
    padding: ({ miniEvent }) => (miniEvent ? theme.spacing(0, 1.25) : theme.spacing(1, 1.25)),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: ({ miniEvent }) => (miniEvent ? 'center' : 'flex-start'),
    paddingLeft: theme.spacing(1),
    cursor: 'pointer',
    transition: 'background-color 0.1s ease-in-out',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: ({ surface }) => surface
      }
    }
  },
  allDayRoot: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    marginTop: '2px',
    marginBottom: '2px',
    height: 'calc(100% - 4px)'
  },
  headerRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.spacing(1.25)
  },
  accentPill: {
    width: '3px',
    alignSelf: 'stretch',
    borderRadius: '5px',
    backgroundColor: ({ accentSoft }) => accentSoft,
    flexShrink: 0
  },
  titleLabel: {
    color: ({ accentSoft }) => accentSoft
  },
  timeLabel: {
    color: ({ accentSoft }) => accentSoft
  },
  timeRange: {
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  unavailable: {
    display: 'flex',
    alignItems: 'center !important',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    backgroundImage: getUnavailableStripeBackground(theme),
    '&::after': unavailableBlurOverlay,
    '&:hover $unavailableText': {
      opacity: 1,
      pointerEvents: 'auto'
    }
  },
  unavailableText: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P35,
    color: theme.palette.common.white,
    padding: theme.spacing(1, 2),
    borderRadius: theme.borderRadius.md,
    position: 'relative',
    zIndex: 2,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.2s'
  },
  integrationOverlay: {
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: theme.borderRadius.md,
      backgroundImage: `
        repeating-linear-gradient(
          135deg,
          rgba(0,0,0,0.04),
          rgba(0,0,0,0.04) 10px,
          transparent 10px,
          transparent 13px
        )
      `
    }
  }
}))

export default function DayEvent(props: EventProps<CalendarEvent>) {
  const { events: contextEvents, updateEvent, deleteEvent } = useCalendar()
  const { options } = useClientOptions()
  const { users } = useUsers()
  const { event } = props
  const { start, end, estimator, contact } = event
  const miniEvent = moment(end).diff(moment(start), 'minutes') <= 30
  const palette = getCrewPalette(estimator?.color)
  const classes = useStyles({ ...palette, miniEvent })
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const eventType =
    event.eventType === GOOGLE_CALENDAR_EVENT_TYPE
      ? event.title
      : translateEventType({ eventType: event.eventType, options })

  const { openDialog, dismissDialog } = useDialogs()
  const cancelEventFlow = useCancelEventFlow()

  const handleEditEvent = () => {
    // Calendar.tsx shifts event.start/end so the time grid renders in company tz;
    // resolve back to the original UTC event so EventDialog's `.tz(timezone)`
    // formatting renders the real scheduled time.
    const originalEvent = contextEvents.find((evt) => evt.id === event.id) ?? event
    openDialog(EventDialog, {
      event: originalEvent,
      onDelete: deleteEvent,
      onConfirm: (updatedEvent: CalendarEvent, eventTemplate: string) => {
        updateEvent(updatedEvent, eventTemplate)
        dismissDialog()
        setAnchorEl(null)
      }
    })
  }

  const handleCancelEvent = (event: CalendarEvent) =>
    cancelEventFlow(event, (evt, template) => {
      deleteEvent(evt, template)
      setAnchorEl(null)
    })

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setAnchorEl(anchorEl ? null : e.currentTarget)
  }

  const DayEventContent = () => {
    if (event.allDay) {
      return (
        <Typography variant="body1" weight={600} noWrap className={classes.titleLabel} style={{ minWidth: 0, flex: 1 }}>
          {event.title || eventType}
        </Typography>
      )
    }
    if (miniEvent) {
      return (
        <Typography variant="body2" weight={600} className={classes.titleLabel}>
          {eventType}
          {contact && <> - {formatName(contact)}</>}
        </Typography>
      )
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Typography variant="body1" weight={600} className={classes.titleLabel}>
          {eventType}
          {contact && <> - {formatName(contact)}</>}
        </Typography>
        <Typography variant="body2" className={classes.timeLabel}>
          {getEventLabel(moment(event.start), moment(event.end), false)}
        </Typography>
      </div>
    )
  }

  if (eventType === '_unavailable_') {
    return (
      <div className={classnames(classes.root, classes.unavailable, 'rbc-event-unavailable')}>
        <Typography variant="body2" className={classes.unavailableText} weight={500}>
          Unavailable
        </Typography>
      </div>
    )
  }

  return (
    <>
      <div
        className={classnames(
          classes.root,
          event.allDay ? classes.allDayRoot : null,
          event.eventType === GOOGLE_CALENDAR_EVENT_TYPE ? classes.integrationOverlay : null,
          event.eventType === GOOGLE_CALENDAR_EVENT_TYPE ? 'rbc-event-imported' : null
        )}
        onClick={handleClick}
      >
        <div className={classes.headerRow}>
          <span className={classes.accentPill} />
          <DayEventContent />
        </div>
      </div>
      <EventPreviewPopper
        event={contextEvents.find((evt) => evt.id === event.id) ?? event}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        users={users}
        onClose={() => setAnchorEl(null)}
        onEditEvent={handleEditEvent}
        onCancelEvent={handleCancelEvent}
        onNavigate={(path) => history.push(path)}
        minWidth={330}
      />
    </>
  )
}
